import { GridCellParams, GridPreProcessEditCellProps } from '@mui/x-data-grid-premium';
import { GridColDef } from '@mui/x-data-grid-pro';
import clsx from 'clsx';
import { Money } from 'sr-types/lib/production/v1/graphql';
import { useI18n } from '../../i18n/I18n';
import { formatMoney, isMoney, moneyToBigNumber, parseMoney } from '../../utils/money';
import { isNumberValid } from '../../utils/numbers';
import { UseColDefProps } from './UseColDefProps';
import { AdjustmentLine } from './DiscountGrid';
import { DiscountCalculationTypesEnum } from './costLineAdapter';

const discountValueGetter = () => (value, row) => {
    const isPercent = row.calculationType === DiscountCalculationTypesEnum.pct;

    if (isPercent) {
        if (isMoney(value)) {
            return '';
        }
        return value;
    } else {
        return isMoney(value) ? moneyToBigNumber(value as Money).toString() : '';
    }
};

const discountValueSetter =
    (
        calculator,
        name: string,
        prop: string,
        locale: string,
        rows: AdjustmentLine[],
        onChange: (pricing: AdjustmentLine[]) => void
    ) =>
    (value, row) => {
        const currencyCode = row.currency;
        const isPercent = row.calculationType === DiscountCalculationTypesEnum.pct;
        const money = currencyCode && !isPercent ? parseMoney(value, { locale, currencyCode }) : value;
        let updatedRow;
        if (isMoney(money) && !isPercent) {
            updatedRow = calculator.calculate({ ...row, [prop]: money });
        } else {
            updatedRow = calculator.calculate({ ...row, [prop]: value });
        }
        if (row) {
            const updated = rows.map((r: AdjustmentLine) => (r.id === row.id ? updatedRow : r));
            onChange(updated);
        }
        return updatedRow;
    };

const discountValueParser = () => (value) => {
    return value;
};

const discountValueFormatter = (locale: string) => (value, row) => {
    const currencyCode = row.currency;
    const isPercent = row.calculationType === DiscountCalculationTypesEnum.pct;
    return isPercent
        ? value
            ? value + '%'
            : ''
        : currencyCode
          ? formatMoney(parseMoney(value, { locale, currencyCode }), {
                locale: locale,
                showCurrencySymbol: true
            })
          : '';
};

export default (props: UseColDefProps): GridColDef => {
    const {
        name,
        prop,
        locale,
        width,
        readOnly = false,
        rightAlign = true,
        flex = 0,
        setIsRowValid,
        calculator,
        classNames,
        rows,
        setRows: onChange
    } = props;
    const header = useI18n(`grid.header.${prop}`);
    const align = rightAlign ? 'right' : 'left';

    return {
        field: prop,
        headerName: header,
        editable: !readOnly,
        flex: flex,
        width: width,
        type: 'string',
        align: align,
        headerAlign: align,
        cellClassName: (params: GridCellParams) => {
            const isFlat = params.row.calculationType === DiscountCalculationTypesEnum.flt;
            return clsx(prop, classNames, {
                isEmpty: !readOnly && isFlat ? !isMoney(params.row[params.field]) : !isFinite(params.row[params.field])
            });
        },
        valueFormatter: discountValueFormatter(locale),
        valueGetter: discountValueGetter(),
        valueSetter: readOnly
            ? undefined
            : discountValueSetter(calculator, name, prop, locale, rows as AdjustmentLine[], onChange),
        valueParser: readOnly ? undefined : discountValueParser(),
        preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
            const valid = isNumberValid(params.props.value, locale);
            if (typeof setIsRowValid === 'function') {
                setIsRowValid(params.id as string, prop, valid);
            }
            return { ...params.props, error: !valid };
        }
    };
};
